/**
 * Render 4×6 PDF to ImageData at 203 DPI using PDF.js (browser).
 * Loads PDF.js from CDN to avoid "Failed to resolve pdfjs-dist" in environments
 * where the npm package is not installed (e.g. some cloud IDEs).
 * Scale-to-fit, optional margins, no cropping.
 */

const PDFJS_VERSION = '5.4.530';
const PDFJS_CDN = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}`;

let pdfjsLib: {
  getDocument: (opts: { data: Uint8Array }) => { promise: Promise<{ getPage: (n: number) => Promise<PDFPage> }> };
  GlobalWorkerOptions: { workerSrc: string };
} | null = null;

interface PDFPage {
  getViewport: (opts: { scale: number }) => { width: number; height: number };
  render: (opts: {
    canvasContext: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    viewport: { width: number; height: number };
    intent?: string;
  }) => { promise: Promise<void> };
}

async function loadPdfJs(): Promise<typeof pdfjsLib> {
  if (pdfjsLib) return pdfjsLib;
  const base = PDFJS_CDN;
  pdfjsLib = await import(/* @vite-ignore */ `${base}/build/pdf.mjs`);
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${base}/build/pdf.worker.mjs`;
  return pdfjsLib;
}

const DPI = 203;
const TARGET_WIDTH_PX = 4 * DPI;
const TARGET_HEIGHT_PX = 6 * DPI;
const PT_PER_INCH = 72;

function base64ToUint8Array(base64: string): Uint8Array {
  const bin = atob(base64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

export interface RenderPdfToImageDataOptions {
  widthPx?: number;
  heightPx?: number;
  margin?: number;
  /** Output DPI for rasterization. Default 203. */
  dpi?: number;
}

/**
 * Render PDF page 0 to canvas at target size (default 812×1218), then return ImageData.
 * Preserves aspect ratio; optional margin. Uses options.dpi for raster scale when provided.
 */
export async function renderPdfToImageData(
  pdfBase64: string,
  options: RenderPdfToImageDataOptions = {}
): Promise<{ data: Uint8ClampedArray; width: number; height: number }> {
  const { widthPx = TARGET_WIDTH_PX, heightPx = TARGET_HEIGHT_PX, margin = 0, dpi } = options;
  const rasterDpi = dpi ?? DPI;
  const scale = rasterDpi / PT_PER_INCH;
  const w = Math.max(1, widthPx - 2 * margin);
  const h = Math.max(1, heightPx - 2 * margin);

  const lib = await loadPdfJs();
  const data = base64ToUint8Array(pdfBase64);
  const loading = lib.getDocument({ data });
  const doc = await loading.promise;
  const page = await doc.getPage(1);

  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d not available');

  const fitScale = Math.min(w / viewport.width, h / viewport.height);
  const dx = (w - viewport.width * fitScale) / 2;
  const dy = (h - viewport.height * fitScale) / 2;
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, w, h);
  ctx.save();
  ctx.translate(dx, dy);
  ctx.scale(fitScale, fitScale);

  await page.render({
    canvasContext: ctx,
    canvas,
    viewport,
    intent: 'print',
  }).promise;

  ctx.restore();
  const imageData = ctx.getImageData(0, 0, w, h);
  return { data: imageData.data, width: w, height: h };
}
